import { Currency, DiscountType } from "@lib";
import { IMacro, ISaleMacroOpenContext } from "../macro";
import { MacroAutocomplete } from "@core/controls/document-template-editor/document-template-macros-autocompleter";
import { WarrantyUtils } from "@/utils/types/warranty.utils";
import { companyMacroAutocomplete, CompanyMacroList } from "./company-macro-list";
import { Localizer } from "@/i18n/localizer";
import * as filters from "@/filters";
import { Printer } from "../printer";

export const saleMacroAutocomplete: MacroAutocomplete[] = [
    {
        type: "autocompleteitem",
        text: "Дата",
        value: "дата",
        nested: [{ type: "autocompleteitem", text: "Сегодня", value: "сегодня" }],
    },
    {
        type: "autocompleteitem",
        text: "Продажа",
        value: "продажа",
        nested: [
            { type: "autocompleteitem", text: "Номер", value: "номер" },
            { type: "autocompleteitem", text: "Дата", value: "дата" },
            { type: "autocompleteitem", text: "Сумма скидки", value: "суммаскидки" },
            { type: "autocompleteitem", text: "Комментарий", value: "комментарий" },
        ],
    },
    {
        type: "autocompleteitem",
        text: "Магазин",
        value: "магазин",
        nested: [
            { type: "autocompleteitem", text: "Номер", value: "номер" },
            { type: "autocompleteitem", text: "Название", value: "название" },
            { type: "autocompleteitem", text: "Описание", value: "описание" },
            { type: "autocompleteitem", text: "Телефон", value: "телефон" },
            { type: "autocompleteitem", text: "Почта", value: "почта" },
            { type: "autocompleteitem", text: "Адрес", value: "адрес" },
            { type: "autocompleteitem", text: "Валюта", value: "валюта" },
        ],
    },
    {
        type: "autocompleteitem",
        text: "Товары",
        value: "товары",
        nested: [
            { type: "autocompleteitem", text: "Количество наименований", value: "количествонаименований" },
            { type: "autocompleteitem", text: "Количество", value: "количество" },
            { type: "autocompleteitem", text: "Сумма", value: "сумма" },
            { type: "autocompleteitem", text: "К оплате", value: "коплате" },
            { type: "autocompleteitem", text: "Сумма прописью", value: "суммапрописью" },
            { type: "autocompleteitem", text: "К оплате прописью", value: "коплатепрописью" },
            { type: "autocompleteitem", text: "Дата гарантии", value: "датагарантии" },
        ],
    },
    {
        type: "autocompleteitem",
        text: "Клиент",
        value: "клиент",
        nested: [
            { type: "autocompleteitem", text: "Имя", value: "имя" },
            { type: "autocompleteitem", text: "Телефон", value: "телефон" },
            { type: "autocompleteitem", text: "Почта", value: "почта" },
        ],
    },
    {
        type: "autocompleteitem",
        text: "Продавец",
        value: "продавец",
        nested: [
            { type: "autocompleteitem", text: "Имя", value: "имя" },
            { type: "autocompleteitem", text: "ФИО", value: "фио" },
        ],
    },

    ...companyMacroAutocomplete,
];

export const SaleMacroList: IMacro<ISaleMacroOpenContext>[] = [
    // общее

    {
        alias: ["дата.сегодня", "date.now"],
        open: (context, args) => filters.datetime.datetime(new Date().toUTCString(), "L"),
    },
    ...CompanyMacroList,

    // продажа

    {
        alias: ["продажа.номер", "sale.number"],
        open: (context, args) => {
            const number = context.sale.number.toString();
            let template = context.shop?.settings?.numerationTemplate?.trim();

            if (template && template.length > 0) {
                // чтобы избежать рекурсии
                template = template.replace(/%\(Продажа.Номер\)/gi, number);
                return Printer.replaceSaleDocumentsGeneralMacros(template, context);
            }

            return number;
        },
    },
    {
        alias: ["продажа.дата", "sale.date"],
        open: (context, args) => filters.datetime.datetime(context.sale.createdAt, "L"),
    },
    {
        alias: ["продажа.суммаскидки", "sale.discountvalue"],
        open: (context, args) => {
            const locale = context.shop?.info?.locale;

            if (!context.sale.info?.discount) {
                return filters.money.moneyFormat(0, { locale });
            }

            const discount = context.sale.info?.discount?.value ?? 0;

            if (discount === 0) {
                return filters.money.moneyFormat(discount, { locale });
            }

            if (context.sale.info.discount.type === DiscountType.Fixed) {
                return filters.money.moneyFormat(discount, { locale });
            }

            let sum = 0;
            if (context.sale.goods) {
                for (const good of context.sale.goods) {
                    sum += good.price * good.quantity;
                }
            }

            return filters.money.moneyFormat((sum * discount) / 100, { locale });
        },
    },
    {
        alias: ["продажа.комментарий", "sale.comment"],
        open: (context, args) => context.sale.info?.comment,
    },

    // магазин

    {
        alias: ["магазин.номер", "shop.number"],
        open: (context, args) => context.shop?.number?.toString(),
    },
    {
        alias: ["магазин.название", "shop.name"],
        open: (context, args) => context.shop?.info.name,
    },
    {
        alias: ["магазин.описание", "shop.description"],
        open: (context, args) => context.shop?.info.description,
    },
    {
        alias: ["магазин.телефон", "shop.phone"],
        open: (context, args) => context.shop?.info.contacts?.phone,
    },
    {
        alias: ["магазин.адрес", "shop.address"],
        open: (context, args) => context.shop?.info.contacts?.address,
    },
    {
        alias: ["магазин.почта", "shop.email"],
        open: (context, args) => context.shop?.info.contacts?.email,
    },
    {
        alias: ["магазин.валюта", "shop.currency"],
        open: (context, args) => Localizer.currencySymbol(context.shop?.info.currency ?? Currency.RUB),
    },

    // товары

    {
        alias: ["товары.количествонаименований", "goods.count"],
        open: (context, args) => (context.sale.goods ? context.sale.goods.length.toString() : "0"),
    },
    {
        alias: ["товары.количество", "goods.quantity"],
        open: (context, args) => {
            let quantity = 0;

            if (context.sale.goods) {
                for (const good of context.sale.goods) {
                    quantity += good.quantity;
                }
            }

            return quantity.toString();
        },
    },
    {
        alias: ["товары.сумма", "goods.sum"],
        open: (context, args) => {
            let sum = 0;

            if (context.sale.goods) {
                for (const good of context.sale.goods) {
                    sum += good.price * good.quantity;
                }
            }

            const locale = context.shop?.info?.locale;
            return filters.money.moneyFormat(sum, { locale });
        },
    },
    {
        alias: ["товары.коплате", "goods.forpayment"],
        open: (context, args) => {
            let sum = 0;
            if (context.sale.goods) {
                for (const good of context.sale.goods) {
                    sum += good.price * good.quantity;
                }
            }

            const locale = context.shop?.info?.locale;

            if (!context.sale.info?.discount) {
                return filters.money.moneyFormat(sum, { locale });
            }

            const discount = context.sale.info?.discount?.value ?? 0;
            if (discount === 0) {
                return filters.money.moneyFormat(sum, { locale });
            }

            if (context.sale.info.discount.type === DiscountType.Fixed) {
                return filters.money.moneyFormat(sum - discount, { locale });
            } else if (context.sale.info.discount.type === DiscountType.Percent) {
                return filters.money.moneyFormat(sum * (1 - discount / 100), { locale });
            }

            return filters.money.moneyFormat(sum, { locale });
        },
    },
    {
        alias: ["товары.суммапрописью", "goods.suminwords"],
        open: (context, args) => {
            let sum = 0;

            if (context.sale.goods) {
                for (const good of context.sale.goods) {
                    sum += good.price * good.quantity;
                }
            }

            const currency = context.shop?.info?.currency;
            return filters.money.moneyInWords(sum, currency);
        },
    },
    {
        alias: ["товары.коплатепрописью", "goods.forpaymentinwords"],
        open: (context, args) => {
            let sum = 0;

            if (context.sale.goods) {
                for (const good of context.sale.goods) {
                    sum += good.price * good.quantity;
                }
            }

            const currency = context.shop?.info?.currency;

            if (!context.sale.info?.discount) {
                return filters.money.moneyInWords(sum, currency);
            }

            const discount = context.sale.info?.discount?.value ?? 0;
            if (discount === 0) {
                return filters.money.moneyInWords(sum, currency);
            }

            if (context.sale.info.discount.type === DiscountType.Fixed) {
                return filters.money.moneyInWords(sum - discount, currency);
            } else if (context.sale.info.discount.type === DiscountType.Percent) {
                return filters.money.moneyInWords(sum * (1 - discount / 100), currency);
            }

            return filters.money.moneyInWords(sum, currency);
        },
    },
    {
        alias: ["товары.датагарантии", "товары.warrantydate"],
        open: (context, args) => {
            const warranties = context.sale.goods?.map(w => w.warranty) ?? [];
            return WarrantyUtils.toMaxDateString(warranties);
        },
    },

    // клиент

    {
        alias: ["клиент.имя", "client.name"],
        open: (context, args) => context.client?.info.name,
    },
    {
        alias: ["клиент.телефон", "client.phone"],
        open: (context, args) => context.client?.info.contacts?.phone,
    },
    {
        alias: ["клиент.почта", "client.email"],
        open: (context, args) => context.client?.info.contacts?.email,
    },

    // продавец

    {
        alias: ["продавец.имя", "seller.name"],
        open: (context, args) => context.seller?.info.name,
    },
    {
        alias: ["продавец.фио", "seller.fullname"],
        open: (context, args) => context.seller?.info.officialName,
    },
];
